import { SOCKET_EVENT } from "@sendsar/protocol";
import type { SendsarClient } from "./client.js";
import type { Message } from "./types.js";

export type RoomSubscriptionOptions = {
  roomId: string;
  userId: string;
  limit?: number;
  /** Called with chronological messages after the initial page load. */
  onInitialMessages?: (messages: Message[], peerLastReadAt: string | null) => void;
  onMessage?: (message: Message) => void;
  onMessageUpdated?: (message: Message) => void;
  onPeerLastReadAt?: (lastReadAt: string) => void;
  /** Mark room read when receiving messages from others (default true). */
  autoMarkRead?: boolean;
};

export type RoomSubscription = {
  destroy: () => void;
};

/**
 * Join a room, load message history, and subscribe to live updates.
 * Waits for `connect()` if the client is not connected yet.
 * Call `destroy()` on cleanup — leaves the room and unsubscribes from events.
 */
export function createRoomSubscription(
  client: SendsarClient,
  options: RoomSubscriptionOptions,
): RoomSubscription {
  const {
    roomId,
    userId,
    limit = 50,
    onInitialMessages,
    onMessage,
    onMessageUpdated,
    onPeerLastReadAt,
    autoMarkRead = true,
  } = options;

  let cancelled = false;
  let offConnected: (() => void) | null = null;
  let activated = false;

  async function loadInitialMessages(): Promise<void> {
    if (cancelled || !client.isConnected) return;
    const { messages, peerLastReadAt } = await client.getMessages(roomId, { limit });
    if (cancelled) return;
    onInitialMessages?.([...messages].reverse(), peerLastReadAt);
    const lastId = messages[0]?.id;
    if (lastId) {
      void client.markRoomRead(roomId, lastId);
    }
  }

  function activate(): void {
    if (cancelled || activated || !client.isConnected) return;
    activated = true;
    client.joinRoom({ roomId });
    void loadInitialMessages();
  }

  if (client.isConnected) {
    activate();
  } else {
    offConnected = client.on("connected", () => {
      offConnected?.();
      offConnected = null;
      activate();
    });
  }

  const onNewMessage = (msg: Message) => {
    if (msg.roomId !== roomId) return;
    onMessage?.(msg);
    if (autoMarkRead && msg.senderId !== userId) {
      void client.markRoomRead(roomId, msg.id);
    }
  };

  const onUpdated = (msg: Message) => {
    if (msg.roomId !== roomId) return;
    onMessageUpdated?.(msg);
  };

  const onRoomRead = ({
    roomId: readRoomId,
    userId: readUserId,
    lastReadAt,
  }: {
    roomId: string;
    userId: string;
    lastReadAt: string;
  }) => {
    if (readRoomId !== roomId || readUserId === userId) return;
    onPeerLastReadAt?.(lastReadAt);
  };

  const offNew = client.on(SOCKET_EVENT.NEW_MESSAGE, onNewMessage);
  const offUpdated = client.on(SOCKET_EVENT.MESSAGE_UPDATED, onUpdated);
  const offRoomRead = client.on(SOCKET_EVENT.ROOM_READ, onRoomRead);

  return {
    destroy() {
      cancelled = true;
      offConnected?.();
      offConnected = null;
      offNew();
      offUpdated();
      offRoomRead();
      if (client.isConnected) {
        client.leaveRoom(roomId);
      }
    },
  };
}
